import type { ExportedFile } from '@/core/export/export';

/**
 * Независимая проверка готового файла.
 *
 * Читаем ровно тот файл, который получит пользователь, отдельным декодером
 * (zxing-wasm) и сравниваем результат с ожидаемым содержимым.
 *
 * WASM лежит в public/wasm и грузится со своего origin: путь по умолчанию
 * в zxing-wasm ведёт на jsDelivr, поэтому locateFile переопределён явно.
 */

export type DecodeVerdict =
  | { status: 'match'; decoded: string }
  | { status: 'mismatch'; decoded: string; expected: string }
  | { status: 'not-found' }
  | { status: 'unavailable'; reason: string };

let configured = false;
let assetBase = '';

export function setWasmBase(base: string): void {
  assetBase = base;
  configured = false;
}

async function reader(): Promise<typeof import('zxing-wasm/reader')> {
  const mod = await import('zxing-wasm/reader');
  if (!configured) {
    mod.setZXingModuleOverrides({
      locateFile: (path: string, prefix: string) => {
        if (path.endsWith('.wasm')) return `${assetBase}wasm/zxing_reader.wasm`;
        return prefix + path;
      },
    });
    configured = true;
  }
  return mod;
}

/** Растеризует уже сериализованный SVG, чтобы проверять именно файл, а не сцену. */
export async function rasterizeSvgBlob(blob: Blob, targetWidth: number): Promise<Blob> {
  const url = URL.createObjectURL(blob);
  try {
    const image = new Image();
    image.decoding = 'sync';
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Не удалось открыть сохранённый SVG для проверки.'));
      image.src = url;
    });
    const scale = image.naturalWidth > 0 ? targetWidth / image.naturalWidth : 1;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Нет 2D-контекста для проверки SVG.');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((out) => {
        if (out) resolve(out);
        else reject(new Error('Не удалось растеризовать SVG для проверки.'));
      }, 'image/png');
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Считывает файл независимым декодером и сравнивает с ожидаемой строкой.
 * Статус «unavailable» означает, что проверка не выполнялась —
 * выдавать её за успех нельзя.
 */
export async function verifyExport(file: ExportedFile, expected: string): Promise<DecodeVerdict> {
  try {
    const { readBarcodesFromImageFile } = await reader();
    const source =
      file.format === 'svg'
        ? await rasterizeSvgBlob(file.blob, Math.min(1024, Math.max(512, file.widthPx)))
        : file.blob;

    const results = await readBarcodesFromImageFile(source, {
      tryHarder: true,
      formats: ['QRCode'],
      maxNumberOfSymbols: 1,
    });

    const first = results[0];
    if (!first) return { status: 'not-found' };
    if (first.text === expected) return { status: 'match', decoded: first.text };
    return { status: 'mismatch', decoded: first.text, expected };
  } catch (err) {
    return {
      status: 'unavailable',
      reason: err instanceof Error ? err.message : String(err),
    };
  }
}

export function verdictMessage(verdict: DecodeVerdict): { level: 'ok' | 'error' | 'warning'; text: string } {
  switch (verdict.status) {
    case 'match':
      return { level: 'ok', text: 'Файл успешно считан встроенной проверкой.' };
    case 'mismatch':
      return {
        level: 'error',
        text: 'Проверка прочитала не то содержимое, которое вы ввели. Файл не отдаём.',
      };
    case 'not-found':
      return { level: 'error', text: 'Не удалось проверить считывание: декодер не нашёл QR в файле.' };
    case 'unavailable':
      return {
        level: 'warning',
        text: 'Проверка не выполнялась: декодер недоступен. Это не означает, что файл читается.',
      };
  }
}
